import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useApiMutation";
import {
  dashboardApi,
  DashboardMetricsResponse,
  MetricDetailResponse,
} from "../dashboardApi";
import { DashboardMetricKey } from "../../../enums";
import { useAppContext } from "../../../../AppContext";

const STALE_TIME = 3 * 60 * 1000; // 3 minutes

export interface UseDashboardMetricsParams {
  startDate: string;
  endDate: string;
  enabled?: boolean;
}

export interface UseMetricDetailParams {
  metricKey: DashboardMetricKey;
  startDate: string;
  endDate: string;
  enabled?: boolean;
}

export interface UseToggleMetricParams {
  metricKey: DashboardMetricKey;
}

export const useDashboardMetrics = ({
  startDate,
  endDate,
  enabled = true,
}: UseDashboardMetricsParams) => {
  const { selectedStudioId } = useAppContext();
  const queryParams = dashboardApi.getMetrics.buildQueryParams({
    startDate,
    endDate,
  });

  return useApiQuery<DashboardMetricsResponse>(
    {
      queryKey: [dashboardApi.getMetrics.endpoint, startDate, endDate],
      endpoint: `${dashboardApi.getMetrics.endpoint}?startDate=${queryParams.startDate}&endDate=${queryParams.endDate}`,
      globalIsLoading: false,
      enabled: !!selectedStudioId,
    },
    {
      staleTime: STALE_TIME,
    }
  );
};

export const useMetricDetail = ({
  metricKey,
  startDate,
  endDate,
  enabled = false,
}: UseMetricDetailParams) => {
  const queryParams = dashboardApi.getMetricDetail.buildQueryParams({
    metricKey,
    startDate,
    endDate,
  });

  return useApiQuery<MetricDetailResponse>(
    {
      queryKey: [
        dashboardApi.getMetricDetail.endpoint,
        metricKey,
        startDate,
        endDate,
      ],
      endpoint: `${dashboardApi.getMetricDetail.endpoint}?metricKey=${queryParams.metricKey}&startDate=${queryParams.startDate}&endDate=${queryParams.endDate}`,
      globalIsLoading: false,
      enabled,
    },
    {
      staleTime: STALE_TIME,
    }
  );
};

export const useToggleMetric = () => {
  const disableMetric = useApiMutation({
    onSuccess: () => {
      // Cache invalidation will be handled by the mutation
    },
  });

  const enableMetric = useApiMutation({
    onSuccess: () => {
      // Cache invalidation will be handled by the mutation
    },
  });

  return {
    disableMetric: {
      mutate: (
        params: { metricKey: DashboardMetricKey },
        options?: {
          onSuccess?: () => void;
          onError?: () => void;
          onSettled?: () => void;
        }
      ) => {
        const payload = dashboardApi.disableMetric.buildPayload(params);
        disableMetric.mutate(
          {
            endpoint: dashboardApi.disableMetric.endpoint,
            method: "post",
            invalidateKeys: [[dashboardApi.getMetrics.endpoint]],
            ...payload,
          },
          {
            onSuccess: options?.onSuccess,
            onError: options?.onError,
            onSettled: options?.onSettled,
          }
        );
      },
      isLoading: disableMetric.isLoading,
    },
    enableMetric: {
      mutate: (
        params: { metricKey: DashboardMetricKey },
        options?: {
          onSuccess?: () => void;
          onError?: () => void;
          onSettled?: () => void;
        }
      ) => {
        const payload = dashboardApi.enableMetric.buildPayload(params);
        enableMetric.mutate(
          {
            endpoint: dashboardApi.enableMetric.endpoint,
            method: "post",
            invalidateKeys: [[dashboardApi.getMetrics.endpoint]],
            ...payload,
          },
          {
            onSuccess: options?.onSuccess,
            onError: options?.onError,
            onSettled: options?.onSettled,
          }
        );
      },
      isLoading: enableMetric.isLoading,
    },
  };
};
